import { useAppStore } from '@/hooks/use-app-store';
import type { BottleType, BottleColor } from './use-video-manager';

/**
 * Use this in components where you need to trigger videos based on game events
 */
export const usePotionVideos = () => {
  const { triggerPotionVideo, triggerEffectVideo, triggerCustomVideo, triggerLoopedPotionVideo, triggerCauldronSmokeEffect } = useAppStore();

  return {
    // Potion completion videos
    playPotionVideo: (bottleType: BottleType, color: BottleColor) => {
      console.log(`Playing potion video: ${bottleType} ${color}`);
      triggerPotionVideo(bottleType, color);
    },

    //successful love potion
    playSuccessfulLovePotion: () => {
        triggerPotionVideo('Heart', 'Pink');
    },

    //failed love potion
    playFailedPotion: () => {
        triggerPotionVideo('Normal', 'Black');
    },

    // Game-specific potion results
    playWeirdPotion: () => {
        triggerPotionVideo('Weird', 'Green');
    },

    playMysticPotion: () => {
        triggerPotionVideo('Normal', 'Blue');
    },

    playCitrusPotion: () => {
        triggerPotionVideo('Normal', 'Yellow');
    },

    /* examples of potential functions we could use to call specific potions.
        playPowerfulLovePotion: () => {
        triggerPotionVideo('Heart', 'Rainbow');
        },

        playWeirdPotion: () => {
        triggerPotionVideo('Weird', 'Green');
        },
    */
    // Effect videos for brewing process
    playCauldronEffect: () => {
      console.log('Playing cauldron effect');
      triggerEffectVideo('Cauldron');
    },

    playCauldronSmokeEffect: () => {
      console.log('Playing cauldron smoke effect with splash sound');
      
      // Play the cauldron smoke video effect
      triggerCauldronSmokeEffect();
      
      // Play the splash audio simultaneously
      try {
        const audio = new Audio('/audio/cauldron-splash.wav');
        audio.volume = 0.7; // Adjust volume as needed (0.0 to 1.0)
        audio.play().catch(error => {
          console.warn('Could not play cauldron splash audio:', error);
        });
      } catch (error) {
        console.warn('Error creating cauldron splash audio:', error);
      }
    },

    playScreenSmokeEffect: () => {
      console.log('Playing screen smoke effect with sound');
      
      // Play the screen smoke video effect
      triggerEffectVideo('ScreenSmoke');
      
      // Play the screen smoke audio simultaneously
      try {
        const audio = new Audio('/audio/potions/screensmoke-sound.wav');
        audio.volume = 0.7; // Adjust volume as needed (0.0 to 1.0)
        audio.play().catch(error => {
          console.warn('Could not play screen smoke audio:', error);
        });
      } catch (error) {
        console.warn('Error creating screen smoke audio:', error);
      }
    },

    // Custom video for any other webm file
    playCustomVideo: (videoPath: string) => {
      console.log(`Playing custom video: ${videoPath}`);
      triggerCustomVideo(videoPath);
    },

    // Brewing sequence - play multiple effects in order
    playBrewingSequence: () => {
      // Play cauldron effect first
      triggerEffectVideo('Cauldron');
      
      // After some time, play smoke effect
      setTimeout(() => {
        triggerEffectVideo('CauldronSmoke');
      }, 2000); // 2s for cauldron effect duration
      
      // Finally play screen smoke
      setTimeout(() => {
        triggerEffectVideo('ScreenSmoke');
      }, 4000); // 4s total - gives time for previous effects
    },

    // Custom looped video - plays full video then loops last 3 seconds for 10 seconds
    playBlueHeartLooped: () => {
      console.log('Playing blue heart with custom loop');
      triggerLoopedPotionVideo('Heart', 'Blue', 3, 10);
    },
  };
};
